import { format } from 'date-fns';

// Format date for display
export const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return format(date, 'MMMM dd, yyyy');
  } catch (error) {
    return 'Invalid date';
  }
};

// Format date for relative time
export const formatRelativeDate = (dateString) => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  } catch (error) {
    return 'Unknown';
  }
};

// Calculate reading time based on content
export const calculateReadingTime = (content) => {
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} min read`;
};

// Extract excerpt from content if not provided
export const extractExcerpt = (content, maxLength = 150) => {
  // Remove markdown formatting for excerpt
  const plainText = content
    .replace(/#{1,6}\s+/g, '') // Remove headers
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.*?)\*/g, '$1') // Remove italic
    .replace(/`(.*?)`/g, '$1') // Remove inline code
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
  
  if (plainText.length <= maxLength) return plainText;
  
  // Find the last complete word within the limit
  const truncated = plainText.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  return lastSpaceIndex > 0 
    ? truncated.substring(0, lastSpaceIndex) + '...'
    : truncated + '...';
};

// Search posts by title, content, or tags
export const searchPosts = (posts, query) => {
  if (!query.trim()) return posts;
  
  const searchTerm = query.toLowerCase();
  
  return posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm) ||
    post.content.toLowerCase().includes(searchTerm) ||
    post.excerpt.toLowerCase().includes(searchTerm) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchTerm))
  );
};

// Filter posts by tag
export const filterPostsByTag = (posts, tag) => {
  if (!tag) return posts;
  return posts.filter(post => post.tags.includes(tag));
};

// Get all unique tags from posts
export const getAllTags = (posts) => {
  const allTags = posts.flatMap(post => post.tags);
  return [...new Set(allTags)].sort();
};

// Sort posts by date (newest first)
export const sortPostsByDate = (posts) => {
  return [...posts].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

// Get published posts only
export const getPublishedPosts = (posts) => {
  return posts.filter(post => post.published);
};

// Get related posts based on tags
export const getRelatedPosts = (posts, currentPost, limit = 3) => {
  const otherPosts = posts.filter(post => post.id !== currentPost.id && post.published);
  
  // Score posts based on shared tags
  const scoredPosts = otherPosts.map(post => {
    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
    return {
      ...post,
      score: sharedTags.length
    };
  });
  
  // Sort by score (most shared tags first) then by date
  return scoredPosts
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return new Date(b.publishedAt) - new Date(a.publishedAt);
    })
    .slice(0, limit);
};

// Validate post data
export const validatePost = (post) => {
  const errors = [];
  
  if (!post.title?.trim()) {
    errors.push('Title is required');
  }
  
  if (!post.content?.trim()) {
    errors.push('Content is required');
  }
  
  if (!post.excerpt?.trim()) {
    errors.push('Excerpt is required');
  }
  
  if (!post.tags || post.tags.length === 0) {
    errors.push('At least one tag is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Generate SEO meta tags for a post
export const generatePostMeta = (post) => {
  return {
    title: `${post.title} | TukeWIN Blog`,
    description: post.excerpt,
    keywords: post.tags.join(', '),
    author: post.author,
    publishedTime: post.publishedAt,
    modifiedTime: post.updatedAt
  };
};

// Get next post (newer) in chronological order
export const getNextPost = (currentPost, allPosts) => {
  const sortedPosts = sortPostsByDate(getPublishedPosts(allPosts));
  const currentIndex = sortedPosts.findIndex(post => post.id === currentPost.id);
  
  // Return the previous item in the array (which is newer due to newest-first sorting)
  return currentIndex > 0 ? sortedPosts[currentIndex - 1] : null;
};

// Get previous post (older) in chronological order
export const getPreviousPost = (currentPost, allPosts) => {
  const sortedPosts = sortPostsByDate(getPublishedPosts(allPosts));
  const currentIndex = sortedPosts.findIndex(post => post.id === currentPost.id);
  
  // Return the next item in the array (which is older due to newest-first sorting)
  return currentIndex < sortedPosts.length - 1 ? sortedPosts[currentIndex + 1] : null;
};